<template>
  <div
    class="h-full w-16 bg-white dark:bg-slate-900 border-r border-slate-50 dark:border-slate-800/50 rtl:border-l rtl:border-r-0 flex justify-between flex-col"
  >
    <div class="flex flex-col items-center">
      <logo
        :source="logoSource"
        :name="installationName"
        :account-id="accountId"
        class="m-4 mb-2"
      />
      <primary-nav-item
        v-for="menuItem in menuItems"
        :key="menuItem.toState"
        :icon="menuItem.icon"
        :name="menuItem.label"
        :to="menuItem.toState"
        :is-child-menu-active="menuItem.key === activeMenuItem"
      />
    </div>
    <div class="flex flex-col items-center justify-end pb-6">
      <primary-nav-item
        v-if="!isACustomBrandedInstance"
        class="hidden"
        icon="book-open-globe"
        name="DOCS"
        :open-in-new-page="true"
        :to="helpDocsURL"
      />
      <span
        v-if="isAdmin"
        id="updates-toolzz"
        v-tooltip.right="$t('SIDEBAR.CHANGELOG')"
        class="text-slate-700 dark:text-slate-100 w-10 h-10 my-2 flex items-center justify-center relative"
      >
        <fluent-icon
          v-if="!toolzzUpdates"
          icon="changelog"
          size="20"
          type="outline"
        />
      </span>
      <notification-bell @open-notification-panel="openNotificationPanel" />
      <agent-details @toggle-menu="toggleOptions" />
      <options-menu
        :show="showOptionsMenu"
        @toggle-accounts="toggleAccountModal"
        @show-support-chat-window="toggleSupportChatWindow"
        @key-shortcut-modal="$emit('key-shortcut-modal')"
        @close="toggleOptions"
      />
    </div>
  </div>
</template>
<script>
import Logo from './Logo.vue';
import PrimaryNavItem from './PrimaryNavItem.vue';
import OptionsMenu from './OptionsMenu.vue';
import AgentDetails from './AgentDetails.vue';
import NotificationBell from './NotificationBell.vue';
import adminMixin from 'dashboard/mixins/isAdmin';
import wootConstants from 'dashboard/constants/globals';
import { frontendURL } from 'dashboard/helper/URLHelper';
import { ACCOUNT_EVENTS } from '../../../helper/AnalyticsHelper/events';

const TOOLZZ_UPDATES_SRC = 'https://updates.toolzz.dev/widget.js';

export default {
  components: {
    Logo,
    PrimaryNavItem,
    OptionsMenu,
    AgentDetails,
    NotificationBell,
  },
  mixins: [adminMixin],
  props: {
    isACustomBrandedInstance: {
      type: Boolean,
      default: false,
    },
    logoSource: {
      type: String,
      default: '',
    },
    installationName: {
      type: String,
      default: '',
    },
    accountId: {
      type: Number,
      default: 0,
    },
    menuItems: {
      type: Array,
      default: () => [],
    },
    activeMenuItem: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      helpDocsURL: wootConstants.DOCS_URL,
      showOptionsMenu: false,
      toolzzUpdates: null,
      toolzzUpdatesInit: null,
    };
  },
  watch: {
    isAdmin: {
      immediate: true,
      handler(isAdmin) {
        if (isAdmin) this.$nextTick(this.mountToolzzUpdates);
        else this.destroyToolzzUpdates();
      },
    },
  },
  beforeDestroy() {
    this.destroyToolzzUpdates();
  },
  methods: {
    frontendURL,
    toggleOptions() {
      this.showOptionsMenu = !this.showOptionsMenu;
    },
    toggleAccountModal() {
      this.$emit('toggle-accounts');
    },
    toggleSupportChatWindow() {
      window.$chatwoot.toggle();
    },
    openNotificationPanel() {
      this.$track(ACCOUNT_EVENTS.OPENED_NOTIFICATIONS);
      this.$emit('open-notification-panel');
    },
    // Manual init/destroy instead of the script's auto-init: the sidebar
    // remounts (logout/login, role change) and the widget must follow.
    mountToolzzUpdates() {
      if (!this.isAdmin || this.toolzzUpdates) return;
      const init = () => {
        if (!this.isAdmin || this.toolzzUpdates) return;
        this.toolzzUpdates = window.ToolzzUpdates.init({
          product: 'chat',
          target: '#updates-toolzz',
          theme: 'class',
          panelAlign: 'left',
          panelDirection: 'up',
        });
      };
      if (window.ToolzzUpdates) {
        init();
        return;
      }
      let script = document.querySelector(
        `script[src="${TOOLZZ_UPDATES_SRC}"]`
      );
      if (!script) {
        script = document.createElement('script');
        script.src = TOOLZZ_UPDATES_SRC;
        script.async = true;
        script.onerror = () => script.remove();
        document.body.appendChild(script);
      }
      this.toolzzUpdatesInit = init;
      script.addEventListener('load', init);
    },
    destroyToolzzUpdates() {
      if (this.toolzzUpdatesInit) {
        document
          .querySelector(`script[src="${TOOLZZ_UPDATES_SRC}"]`)
          ?.removeEventListener('load', this.toolzzUpdatesInit);
        this.toolzzUpdatesInit = null;
      }
      if (this.toolzzUpdates) this.toolzzUpdates.destroy();
      this.toolzzUpdates = null;
    },
  },
};
</script>

<style>
#updates-toolzz > * {
  cursor: pointer;
}
</style>
